import { useState } from 'react';
import { Alert, Button, TextField } from '../components/ui';
import type { InvoiceState } from '../pos/useInvoices';

/** Dieselbe Form wie die Prüfung des Servers (zod `email`): Der Server weist sonst mit 400 ab. */
const EMAIL =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;
export const looksLikeEmail = (value: string): boolean => value.length <= 254 && EMAIL.test(value);

/**
 * Kaufbeleg per E-Mail nach dem Kassieren (Welle 5d). Zeigt nur an: Die Adresse steht nur im Eingabefeld und wird beim Absenden
 * sofort geleert; die Seite gibt sie an die Warteschlange der Kasse, die sie nach dem Versand vom Gerät löscht.
 */
export function InvoiceBox({
  state,
  offline,
  onSend,
  onDismiss,
}: {
  state: InvoiceState | undefined;
  offline: boolean;
  onSend: (email: string) => Promise<void>;
  onDismiss: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (state?.kind === 'waiting') {
    return (
      <p className="muted" role="status">
        {offline
          ? 'Kaufbeleg: Wird gesendet, sobald online. Die Adresse liegt bis dahin nur auf diesem Gerät.'
          : 'Kaufbeleg: Wird gesendet …'}
      </p>
    );
  }
  if (state?.kind === 'blocked') {
    return (
      <Alert kind="info">{`Kaufbeleg: Senden derzeit nicht möglich: ${state.message} Die Kasse versucht es weiter.`}</Alert>
    );
  }
  if (state?.kind === 'sent') {
    return (
      <div className="stack" role="status">
        <p>Kaufbeleg wurde per E-Mail gesendet.</p>
        <Button variant="secondary" small onClick={onDismiss}>
          Weiteren Kaufbeleg senden
        </Button>
      </div>
    );
  }
  if (state?.kind === 'failed') {
    return (
      <div className="stack">
        <Alert kind="error">{state.message}</Alert>
        <Button variant="secondary" small onClick={onDismiss}>
          Noch einmal versuchen
        </Button>
      </div>
    );
  }
  if (!open) {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        Kaufbeleg per E-Mail
      </Button>
    );
  }
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(submit) => {
        submit.preventDefault();
        const address = email.trim();
        if (!looksLikeEmail(address)) {
          setError('Bitte eine gültige E-Mail-Adresse eingeben.');
          return;
        }
        // Die Adresse verlässt die Maske sofort: Danach liegt sie nur noch in der Warteschlange, bis sie gesendet ist
        setEmail('');
        setError(null);
        setOpen(false);
        void onSend(address);
      }}
    >
      <TextField
        label="E-Mail-Adresse für den Kaufbeleg"
        type="email"
        inputMode="email"
        autoComplete="off"
        value={email}
        error={error ?? undefined}
        hint="Die Adresse wird nur für den Versand verwendet und danach vom Gerät gelöscht. Der Kaufbeleg ist keine Rechnung und kein steuerlicher Beleg."
        onChange={(change) => {
          setEmail(change.target.value);
        }}
      />
      <div className="row">
        <Button type="submit">Senden</Button>
        <Button
          variant="secondary"
          onClick={() => {
            setEmail('');
            setError(null);
            setOpen(false);
          }}
        >
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

/**
 * Statuszeilen für Kaufbelege, auch nach „Nächster Kunde“: wartende und gesperrte stehen da, bis sie gesendet sind; gesendete und
 * fehlgeschlagene bleiben, bis die Kassenkraft sie wegklickt.
 */
export function InvoiceStatusBar({
  entries,
  offline,
  onDismiss,
}: {
  entries: readonly { saleClientUuid: string; state: InvoiceState }[];
  offline: boolean;
  onDismiss: (saleClientUuid: string) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <>
      {entries.map(({ saleClientUuid, state }) => {
        const label =
          `Kaufbeleg für Verkauf ${state.seq === undefined ? '' : `Nr. ${String(state.seq)}`}`.trim();
        if (state.kind === 'waiting') {
          return (
            <Alert key={saleClientUuid} kind="info">
              {`${label}: ${offline ? 'wird gesendet, sobald online' : 'wird gesendet'}`}
            </Alert>
          );
        }
        if (state.kind === 'blocked') {
          return (
            <Alert key={saleClientUuid} kind="error">
              {`${label}: Senden derzeit nicht möglich: ${state.message}`}
            </Alert>
          );
        }
        return (
          <Alert key={saleClientUuid} kind={state.kind === 'sent' ? 'success' : 'error'}>
            <div className="row">
              <span>
                {state.kind === 'sent'
                  ? `${label}: gesendet`
                  : `${label}: Fehler: ${state.message}`}
              </span>
              <Button
                small
                variant="secondary"
                onClick={() => {
                  onDismiss(saleClientUuid);
                }}
              >
                OK
              </Button>
            </div>
          </Alert>
        );
      })}
    </>
  );
}
